import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, MapPin, Award, Users, Briefcase, Globe, Building2, ArrowRight, CheckCircle } from 'lucide-react';

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.12 } } };

const LEADERS = [
  {
    name: 'Kundan Kumar',
    role: 'Co-Founder & Director',
    tagline: 'Technology Architect & Product Visionary',
    bio: 'Kundan leads Vibhraad\'s technical strategy and product development. With a deep passion for building scalable enterprise systems, he drives the engineering culture and oversees delivery across all software verticals — from ERP and CRM systems to custom mobile and web applications.',
    expertise: [
      'Enterprise Software Architecture',
      'Full-Stack Development',
      'Product Strategy & Roadmapping',
      'DevOps & Cloud Infrastructure',
      'Team Leadership & Mentorship',
    ],
    color: 'blue',
    gradient: 'from-blue-600 to-blue-800',
    initials: 'KK',
  },
  {
    name: 'Jeetendra Yadav',
    role: 'Co-Founder & Director',
    tagline: 'Business Strategist & Client Success Lead',
    bio: 'Jeetendra shapes Vibhraad\'s business growth, client relationships, and go-to-market strategy. He brings a strong foundation in operations and digital business development, ensuring every client engagement delivers measurable impact and long-term value.',
    expertise: [
      'Business Development & Growth',
      'Client Relationship Management',
      'Digital Marketing Strategy',
      'Operations & Process Design',
      'Partnership & Alliance Building',
    ],
    color: 'indigo',
    gradient: 'from-indigo-600 to-indigo-800',
    initials: 'JY',
  },
];

const COMPANY_STATS = [
  { value: '2025', label: 'Founded', icon: Building2 },
  { value: '25+', label: 'Countries Served', icon: Globe },
  { value: '500+', label: 'Projects Delivered', icon: Briefcase },
  { value: '100+', label: 'Happy Clients', icon: Users },
];

const VALUES = [
  { title: 'Transparency', desc: 'We operate with complete openness — no hidden costs, no scope surprises, no ambiguity.' },
  { title: 'Excellence', desc: 'We hold ourselves to a standard of quality that creates software you\'re proud to show your customers.' },
  { title: 'Partnership', desc: 'We build long-term relationships, not one-off transactions. Your growth is our success.' },
  { title: 'Innovation', desc: 'We stay ahead of technology trends so our clients always have access to best-in-class solutions.' },
];

function LeaderCard({ leader, index }) {
  const isBlue = leader.color === 'blue';
  return (
    <motion.div
      variants={fade}
      className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-500"
    >
      {/* Card Header */}
      <div className={`bg-gradient-to-br ${leader.gradient} p-8 relative overflow-hidden`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute border border-white rounded-full"
              style={{ width: `${80 + i * 60}px`, height: `${80 + i * 60}px`, top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
          ))}
        </div>
        <div className="relative flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-white/30">
            <span className="text-white text-3xl font-black">{leader.initials}</span>
          </div>
          <div>
            <h3 className="text-2xl font-black text-white mb-1">{leader.name}</h3>
            <p className="text-white/80 text-sm font-semibold mb-1">{leader.role}</p>
            <p className="text-white/60 text-xs">{leader.tagline}</p>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-8">
        <p className="text-gray-600 text-sm leading-relaxed mb-6">{leader.bio}</p>

        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Areas of Expertise</h4>
        <ul className="space-y-2 mb-6">
          {leader.expertise.map((item) => (
            <li key={item} className="flex items-center gap-2.5">
              <CheckCircle className={`w-4 h-4 flex-shrink-0 ${isBlue ? 'text-blue-500' : 'text-indigo-500'}`} />
              <span className="text-gray-700 text-sm">{item}</span>
            </li>
          ))}
        </ul>

        <div className="flex gap-3">
          <a
            href="mailto:vibhraad@gmail.com"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              isBlue
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-indigo-600 hover:bg-indigo-500 text-white'
            }`}
          >
            <Mail className="w-4 h-4" /> Contact
          </a>
          <a
            href="https://www.linkedin.com/company/vibhraad/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-all"
          >
            <Linkedin className="w-4 h-4" /> LinkedIn
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function LeadershipPage() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gray-950 py-24 relative overflow-hidden">
        {/* Dot grid */}
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle, #4B5563 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-700/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-56 h-56 bg-indigo-700/20 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.7 } }}>
            <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-semibold px-4 py-2 rounded-full mb-6">
              <Award className="w-3.5 h-3.5" /> Our Leadership
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight">
              Built by Founders.<br />
              <span className="text-blue-400">Driven by Purpose.</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Vibhraad Technology was founded by two passionate technologists with a shared mission — to make enterprise-grade software accessible to businesses of every size.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="bg-blue-600 py-12">
        <div className="max-w-5xl mx-auto px-5">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {COMPANY_STATS.map(({ value, label, icon: Icon }) => (
              <motion.div key={label} variants={fade} className="text-center">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="text-3xl font-black text-white mb-1">{value}</div>
                <div className="text-blue-100 text-xs font-medium">{label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Leadership Cards */}
      <section className="py-20 max-w-5xl mx-auto px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Meet the Directors</h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto">Two visionaries, one shared goal — delivering technology that transforms businesses.</p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8"
        >
          {LEADERS.map((leader, i) => (
            <LeaderCard key={leader.name} leader={leader} index={i} />
          ))}
        </motion.div>
      </section>

      {/* Company Info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-black text-gray-900 mb-3">Our Story</h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto">
              Founded in October 2025 in Noida, India, Vibhraad Technology was born from a simple belief: every business deserves software that actually fits their needs — not off-the-shelf compromises.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Values */}
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="space-y-4"
            >
              <h3 className="text-xl font-black text-gray-900 mb-5">What We Stand For</h3>
              {VALUES.map(({ title, desc }) => (
                <motion.div key={title} variants={fade} className="flex gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm mb-1">{title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Company Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-950 rounded-3xl p-8 text-white"
            >
              <h3 className="text-xl font-black mb-6">Company Information</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Registered Name</p>
                  <p className="text-white font-semibold text-sm">Vibhraad Technology Private Limited</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">CIN</p>
                  <p className="text-white font-mono text-sm">U62091UP2025PTC234668</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Founded</p>
                  <p className="text-white font-semibold text-sm">October 2025</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Headquarters</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 text-sm leading-relaxed">F-375, Sector-36, Noida, Uttar Pradesh 201301, India</p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Branch Office</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 text-sm leading-relaxed">3rd Floor, Kalpana Hardware Building, Shivpur, Mahurai, UP</p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
                  <a href="mailto:vibhraad@gmail.com" className="text-blue-400 hover:text-blue-300 text-sm transition-colors">vibhraad@gmail.com</a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Ready to Work With Us?</h2>
            <p className="text-gray-500 text-base mb-8 max-w-lg mx-auto">
              Let's discuss how Vibhraad Technology can help you build, scale, or transform your digital product.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 py-3.5 rounded-xl transition-all shadow-lg shadow-blue-600/25"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 font-semibold px-7 py-3.5 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-all"
              >
                Learn More About Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
